import Link, { LinkProps } from "next/link";
import { AnchorHTMLAttributes, forwardRef, PropsWithChildren, Ref } from "react";

import { useOrgRouter } from "~/utils/paths";

/**
 * Everything `next/link` itself accepts, minus nothing.
 *
 * `LinkProps` is only `InternalLinkProps` — the navigation props. Next's own `Link` is typed as
 * `Omit<AnchorHTMLAttributes, keyof InternalLinkProps> & InternalLinkProps`, and this wrapper
 * spreads `...rest` straight into it, so the anchor half was being forwarded at runtime while the
 * type forbade passing it. That made ordinary things — `className`, `style`, `target`, `aria-*` —
 * a compile error on a component that handles them perfectly well.
 *
 * Mirroring Next's own composition rather than adding props one at a time: the next caller to want
 * `rel` or `onClick` should not have to come back here. Widening breaks no existing caller.
 */
export type OrgLinkProps = PropsWithChildren<Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> & LinkProps>;

export const OrgLink = forwardRef(({ href, ...rest }: OrgLinkProps, ref: Ref<HTMLAnchorElement> | undefined) => {
  const router = useOrgRouter();
  return <Link {...rest} href={router.makeOrgPath(href)} ref={ref} />;
});
OrgLink.displayName = "OrgLink";
